import type { LeaderboardMenu, PropTypes } from './LeaderboardMenu.types';
import type { ReactElement } from 'react';

import { Flex, Separator, Text } from '@rango-dev/whale';
import { t } from 'i18next';
import { CircleDollarSign, CircleStar } from 'lucide-react';
import React, { Fragment } from 'react';
import { Link } from 'react-router-dom';

import { useAppSelector } from '../../../hooks/reduxHooks';
import { AllTimeLeaderboardTab, LeaderboardViewType } from '../../../types';
import { getCampaignMenuItem } from '../../../utils/campaign';
import { CampaignTag } from '../../Campaign/CampaignTag';
import { MenuItem } from '../../Profile/MenuItem';

const MENU_ICON_SIZE = 24;
const CAMPAIGN_MENU_SKELETON_KEYS = ['c1', 'c2', 'c3', 'c4'];

export function getLeaderboardMenu(): LeaderboardMenu {
  return {
    title: t('All Time'),
    id: LeaderboardViewType.ALL_TIME,
    defaultActiveTab: AllTimeLeaderboardTab.SCORE,
    menu: {
      score: {
        title: t('Score'),
        shortTitle: t('Score'),
        link: '/leaderboard/score',
        id: AllTimeLeaderboardTab.SCORE,
        icon: <CircleStar size={MENU_ICON_SIZE} />,
      },
      referral: {
        title: t('Referral'),
        shortTitle: t('Referral'),
        link: '/leaderboard/referral',
        id: AllTimeLeaderboardTab.REFERRAL,
        icon: <CircleDollarSign size={MENU_ICON_SIZE} />,
      },
    },
  };
}

export function LeaderboardMenu(props: PropTypes): ReactElement {
  const { activeTab } = props;
  const leaderboardMenu = getLeaderboardMenu();
  const { data: dynamicCampaigns, loading: campaignsLoading } = useAppSelector(
    (state) => state.campaign.campaigns,
  );
  return (
    <Flex direction="column" gap="5" width="100%">
      <Flex direction="column" gap="3" width="100%">
        <Text size="3" weight="medium" color="gray">
          {leaderboardMenu.title}
        </Text>
        <Flex direction="column" gap="2">
          {Object.values(leaderboardMenu.menu).map((item, idx, all) => {
            const isActive = activeTab === item.id;
            return (
              <Fragment key={item.id}>
                <Link to={item.link}>
                  <MenuItem selected={isActive} title={item.title} prefix={item.icon} />
                </Link>
                {idx < all.length - 1 && <Separator size="4" />}
              </Fragment>
            );
          })}
        </Flex>
      </Flex>
      <Flex direction="column" gap="3" width="100%">
        <Text size="3" weight="medium" color="gray">
          {t('Campaigns')}
        </Text>
        <Flex direction="column" gap="2">
          {campaignsLoading &&
            CAMPAIGN_MENU_SKELETON_KEYS.map((key, idx, all) => (
              <Fragment key={key}>
                <MenuItem loading selected={false} title="" />
                {idx < all.length - 1 && <Separator size="4" />}
              </Fragment>
            ))}
          {dynamicCampaigns?.map((dynamicItem, idx, all) => {
            const dynamicMenuItem = getCampaignMenuItem(dynamicItem);
            const isActive = activeTab === dynamicMenuItem.id;
            return (
              <Fragment key={dynamicMenuItem.id}>
                <Link to={dynamicMenuItem.link}>
                  <MenuItem
                    selected={isActive}
                    title={dynamicMenuItem.title}
                    info={dynamicMenuItem.description}
                    prefix={
                      typeof dynamicMenuItem.icon === 'string' ? (
                        <img
                          src={dynamicMenuItem.icon}
                          alt={dynamicMenuItem.title}
                          width="24px"
                          height="24px"
                        />
                      ) : (
                        dynamicMenuItem.icon
                      )
                    }
                    suffix={<CampaignTag id={dynamicMenuItem.id} />}
                  />
                </Link>
                {idx < all.length - 1 && <Separator size="4" />}
              </Fragment>
            );
          })}
        </Flex>
      </Flex>
    </Flex>
  );
}
